import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getStudentCourse, submitAssignment, resubmitAssignment, downloadSubmission, renameSubmission } from '../../api/studentApi';
import StudentSidebar from '../../components/student/StudentSidebar';
import PageLoader from '../../components/common/PageLoader';
import { useAuth } from '../../auth/AuthContext';
import { toTitleCase } from '../../utils/format';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function StudentCourse() {
  const { courseId } = useParams();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('overview');
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(() => {
    setLoading(true);
    getStudentCourse(courseId).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return (
    <div className="app">
      <StudentSidebar course={null} modules={[]} currentModuleId={null} />
      <div className="main"><div className="body"><PageLoader /></div></div>
    </div>
  );

  const { course, enrollment, modules, assignments = [], assignment_max_mb = 20 } = data;

  const scores = modules.filter(m => m.best_score !== null).map(m => m.best_score);
  const avgQuiz = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  const sidebarCourse = { id: course.id, title: course.title, emoji: course.emoji };
  const sidebarModules = modules.map(m => ({ id: m.id, title: m.title, is_completed: m.is_completed, status: m.status }));

  const allModulesDone          = enrollment.modules_total > 0 && enrollment.modules_done === enrollment.modules_total;
  // Only count modules that actually have quiz questions
  const quizModules      = modules.filter(m => m.question_count > 0);
  const allQuizzesPassed = quizModules.length > 0 && quizModules.every(m => m.best_score !== null && m.best_score >= m.quiz_passmark);
  // Only count assignments that belong to THIS course (assignments array is already course-filtered)
  const allAssignmentsSubmitted = assignments.length > 0 && assignments.every(a => a.is_submitted);
  const allAssignmentsGraded    = assignments.length > 0 && assignments.every(a => a.is_submitted && a.grade);

  return (
    <div className="app">
      <StudentSidebar course={sidebarCourse} modules={sidebarModules} currentModuleId={null} />
      <div className="main" style={{ animation: 'fi .3s ease' }}>

        <div className="ph">
          <div>
            <div className="bc">Student / Courses</div>
            <div className="ps">{toTitleCase(course.instructor || 'Instructor')} · {enrollment.progress}% complete</div>
          </div>
          <button className="btn btn-o btn-sm" onClick={() => navigate('/student/dashboard')}>← My Courses</button>
        </div>

        <div className="body">
          {/* Stat cards */}
          <div className="stats" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="sc">
              <div className="sc-l">Progress</div>
              <div className="sc-v">{enrollment.progress}%</div>
              <div className="sc-bar"><div className="sc-fill" style={{ width: `${enrollment.progress}%`, background: 'var(--yellow)' }} /></div>
            </div>
            <div className="sc">
              <div className="sc-l">Modules</div>
              <div className="sc-v">{enrollment.modules_done}/{enrollment.modules_total}</div>
            </div>
            <div className="sc">
              <div className="sc-l">Avg Quiz</div>
              <div className="sc-v">{avgQuiz !== null ? `${avgQuiz}%` : '—'}</div>
              {avgQuiz !== null && <div className="sc-bar"><div className="sc-fill" style={{ width: `${avgQuiz}%`, background: 'var(--green)' }} /></div>}
            </div>
            <div className="sc">
              <div className="sc-l">Assignments</div>
              <div className="sc-v">{enrollment.assignments_submitted ?? 0}/{enrollment.assignments_total ?? 0}</div>
            </div>
          </div>


          {/* Tab bar */}
          <div className="tab-bar">
            {[['overview', '◎', 'Overview'], ['modules', '⊞', 'Modules'], ['assignments', '📋', 'Assignments'], ['certificate', '🎓', 'Certificate']].map(([key, icon, label]) => (
              <div key={key} className={`tab${tab === key ? ' on' : ''}`} onClick={() => setTab(key)}>
                {icon} {label}
              </div>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="card">
              <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 800, fontSize: 20, marginBottom: 16 }}>
                {toTitleCase(course.title)}
              </div>
              <div className="ct">About this Course</div>
              {course.description ? (
                <div className="rich-text" style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: course.description }} />
              ) : (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>No description available.</p>
              )}
            </div>
          )}

          {tab === 'modules' && (
            <ModulesTab modules={modules} courseId={courseId} navigate={navigate} enrollment={enrollment} />
          )}
          {tab === 'assignments' && (
            <AssignmentsTab
              assignments={assignments}
              courseId={courseId}
              maxMb={assignment_max_mb}
              onRefresh={load}
            />
          )}
          {tab === 'certificate' && (
            <CertificateTab
              enrollment={enrollment}
              course={course}
              allModulesDone={allModulesDone}
              allQuizzesPassed={allQuizzesPassed}
              allAssignmentsSubmitted={allAssignmentsSubmitted}
              allAssignmentsGraded={allAssignmentsGraded}
              user={user}
              onGoModules={() => setTab('modules')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ModulesTab({ modules, courseId, navigate, enrollment }) {
  if (modules.length === 0) return (
    <div className="card" style={{ textAlign: 'center', padding: 36, color: 'var(--muted)', fontSize: 13 }}>
      No modules published yet.
    </div>
  );

  return (
    <div className="card">
      <div className="ct">
        <span>Course Modules</span>
        <span className="b b-y">{enrollment.modules_done}/{enrollment.modules_total} completed</span>
      </div>
      {modules.map((m, i) => {
        const isDone    = m.status === 'done';
        const isCurrent = m.status === 'current';
        const isLocked  = m.status === 'locked';

        return (
          <div
            key={m.id}
            className={`mi${isLocked ? ' locked' : ''}`}
            style={{ animation: 'slideUp .25s ease both', animationDelay: `${i * 0.05}s` }}
            onClick={() => !isLocked && navigate(`/student/courses/${courseId}/modules/${m.id}`)}
          >
            <div className={`mn${isDone ? ' done' : isCurrent ? ' active' : ''}`}>
              {isDone ? '✓' : i + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div className="mt">{toTitleCase(m.title)}</div>
              {m.overview && (() => {
                const plain = m.overview.replace(/<[^>]*>/g, '').trim();
                return plain ? <div className="ms">{plain.length > 65 ? plain.slice(0, 65) + '…' : plain}</div> : null;
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={`b ${isDone ? 'b-g' : isCurrent ? 'b-o' : 'b-gr'}`}>
                {isDone ? 'Completed' : isCurrent ? 'Start' : 'Locked'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* file icon by extension */
function fileIcon(name = '') {
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf')                      return '📄';
  if (['doc','docx'].includes(ext))       return '📝';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['ppt','pptx'].includes(ext))       return '📑';
  if (ext === 'zip')                      return '🗜️';
  return '📎';
}

function fmtBytes(bytes) {
  if (!bytes && bytes !== 0) return null;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024)     return `${Math.round(bytes / 1_024)} KB`;
  return `${bytes} B`;
}

function AssignmentCard({ a, i, courseId, maxMb, onRefresh }) {
  const fileInputRef                    = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState('');
  const [renaming,     setRenaming]     = useState(false);
  const [renameVal,    setRenameVal]    = useState('');
  const [downloading,  setDownloading]  = useState(false);

  const daysLeft = a.due_date
    ? Math.ceil((new Date(a.due_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  let badge;
  if (a.grade)              badge = <span className="b b-g">Graded: {a.grade}</span>;
  else if (a.is_submitted)  badge = <span className="b b-o">Submitted</span>;
  else if (daysLeft > 0)    badge = <span className="b b-y">{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</span>;
  else                      badge = <span className="b b-r">Past Due</span>;

  /* ── helpers ── */
  function getBase(name = '') {
    const d = name.lastIndexOf('.');
    return d > 0 ? name.slice(0, d) : name;
  }
  function getExt(name = '') {
    const d = name.lastIndexOf('.');
    return d > 0 ? name.slice(d) : '';   // includes dot
  }

  function onFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const limitBytes = (maxMb || 20) * 1024 * 1024;
    if (file.size > limitBytes) {
      setError(`File too large. Maximum allowed size is ${maxMb || 20} MB.`);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    setError('');
  }

  async function handleSubmit() {
    if (!selectedFile) { setError('Please select a file before submitting.'); return; }
    setSubmitting(true); setError('');
    try {
      await submitAssignment(courseId, a.id, selectedFile);
      setSelectedFile(null);
      onRefresh();
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally { setSubmitting(false); }
  }

  async function handleResubmit() {
    try { await resubmitAssignment(courseId, a.id); onRefresh(); }
    catch (err) { setError(err.message); }
  }

  async function handleDownload() {
    setDownloading(true);
    try { await downloadSubmission(courseId, a.id, a.file_name); }
    catch (err) { setError(err.message); }
    finally { setDownloading(false); }
  }

  async function handleRename() {
    const trimmed = renameVal.trim();
    if (!trimmed) return;
    try {
      await renameSubmission(courseId, a.id, trimmed);
      setRenaming(false);
      onRefresh();
    } catch (err) { setError(err.message); }
  }

  /* ── derived ── */
  const fileExt  = getExt(a.file_name || '');
  const baseName = getBase(a.file_name || '');
  const types    = (a.file_types || []).map(t => t.toUpperCase());

  return (
    <div className="card" style={{ animation: 'slideUp .25s ease both', animationDelay: `${i * 0.06}s` }}>
      {/* Header */}
      <div className="ct">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span>{a.title}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>{badge}</div>
      </div>

      {a.description && (
        <div className="rich-text" style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.65, marginBottom: 14 }}
          dangerouslySetInnerHTML={{ __html: a.description }} />
      )}
      {a.link && (
        <div style={{ marginBottom: 14 }}>
          <a href={a.link} target="_blank" rel="noreferrer"
            style={{ fontSize: 13, color: '#1A56DB', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'underline', textUnderlineOffset: 3 }}>
            🔗 Reference Link ↗
          </a>
        </div>
      )}

      {/* Info row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
        {[['Due Date', fmtDate(a.due_date)], ['Max Score', `${a.max_score} pts`], ['File Types', types.join(', ') || '—']].map(([lbl, val]) => (
          <div key={lbl} style={{ padding: '10px 12px', background: 'var(--surface)', borderRadius: 'var(--rs)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 }}>{lbl}</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{val}</div>
          </div>
        ))}
      </div>

      {/* ── SUBMITTED STATE ── */}
      {a.is_submitted ? (<>

        {/* File row — matches resource file style */}
        <div className="uf">
          <div className="uf-icon" style={{ background: 'var(--surface)', fontSize: 18 }}>
            {fileIcon(a.file_name)}
          </div>

          {renaming ? (
            /* Inline rename */
            <>
              <input
                className="li"
                style={{ flex: 1, margin: 0, fontSize: 13, padding: '5px 9px', minWidth: 0 }}
                value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
                autoFocus
              />
              <span style={{ fontSize: 13, color: 'var(--muted)', whiteSpace: 'nowrap', fontFamily: "'DM Mono',monospace", flexShrink: 0 }}>
                {fileExt}
              </span>
              <button className="btn btn-p btn-sm" onClick={handleRename}>Save</button>
              <button className="uf-rm" onClick={() => setRenaming(false)}>✕</button>
            </>
          ) : (
            /* Normal display */
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="uf-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.file_name}
                </div>
                <div className="uf-size">
                  {a.file_size || '0.0 MB'} · Submitted {fmtDate(a.submitted_at)}
                </div>
              </div>

              {/* Action buttons inline */}
              {!a.grade && (
                <button
                  className="btn btn-o btn-sm"
                  style={{ flexShrink: 0 }}
                  onClick={() => { setRenameVal(baseName); setRenaming(true); }}
                >
                  ✏ Rename
                </button>
              )}
              <button
                className="btn btn-p btn-sm"
                style={{ flexShrink: 0 }}
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? '…' : '⬇ Download'}
              </button>
              {!a.grade && (
                a.instructor_viewed ? (
                  <span className="uf-rm" title="Reviewed by instructor — resubmit locked" style={{ cursor: 'default', opacity: .4 }}>🔒</span>
                ) : (
                  <button className="uf-rm" title="Remove & resubmit" onClick={handleResubmit}>✕</button>
                )
              )}
            </>
          )}
        </div>

        {/* Instructor feedback */}
        {a.grade && a.feedback && (
          <div style={{ marginTop: 12, padding: 14, background: 'var(--green-soft)', border: '1px solid var(--green-border)', borderRadius: 'var(--rs)' }}>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4, fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Instructor Feedback
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0 }}>{a.feedback}</p>
          </div>
        )}

        {error && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 'var(--rs)', fontSize: 12.5, color: '#cf1322' }}>
            {error}
          </div>
        )}

      </>) : (

      /* ── UPLOAD STATE ── */
      <>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept={types.map(t => `.${t.toLowerCase()}`).join(',')}
          onChange={onFileChange}
        />

        {selectedFile ? (
          /* Selected file row — same .uf style */
          <>
            <div className="uf" style={{ borderColor: 'var(--yellow)' }}>
              <div className="uf-icon" style={{ background: 'var(--yellow-soft)', fontSize: 18 }}>
                {fileIcon(selectedFile.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="uf-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedFile.name}
                </div>
                <div className="uf-size">{fmtBytes(selectedFile.size)}</div>
              </div>
              <button className="btn btn-p btn-sm" onClick={handleSubmit} disabled={submitting} style={{ flexShrink: 0 }}>
                {submitting ? 'Uploading…' : '↑ Submit'}
              </button>
              <button className="uf-rm" onClick={() => { setSelectedFile(null); fileInputRef.current.value = ''; }}>✕</button>
            </div>
            {error && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 'var(--rs)', fontSize: 12.5, color: '#cf1322' }}>
                {error}
              </div>
            )}
          </>
        ) : (
          /* Drop zone */
          <div className="uz" onClick={() => fileInputRef.current?.click()}>
            <div className="uz-icon">📎</div>
            <div className="uz-title">Add a submission file</div>
            <div className="uz-sub">Click to browse · Max {maxMb || 20} MB each</div>
            {types.length > 0 && (
              <div className="uz-tags">
                {types.map(t => <span key={t} className="uz-tag">{t}</span>)}
              </div>
            )}
          </div>
        )}
      </>
      )}
    </div>
  );
}

function AssignmentsTab({ assignments, courseId, maxMb, onRefresh }) {
  if (assignments.length === 0) return (
    <div className="card" style={{ textAlign: 'center', padding: '36px', color: 'var(--muted)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
      <p style={{ fontSize: 13 }}>No assignments for this course yet.</p>
    </div>
  );

  return (
    <>
      {assignments.map((a, i) => (
        <AssignmentCard key={a.id} a={a} i={i} courseId={courseId} maxMb={maxMb} onRefresh={onRefresh} />
      ))}
    </>
  );
}

function CertificateTab({ enrollment, course, allModulesDone, allQuizzesPassed, allAssignmentsSubmitted, allAssignmentsGraded, user, onGoModules }) {
  const certRef = useRef(null);

  /* Print — uses @media print CSS that hides everything except .cert-print-root */
  function handlePrint() {
    window.print();
  }

  /* Download PDF — renders cert div to canvas, saves as PDF file directly */
  async function handleDownloadPDF() {
    const certEl = certRef.current;
    if (!certEl) return;
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);
      const canvas = await html2canvas(certEl, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width / 2, canvas.height / 2] });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      pdf.save(`certificate-${enrollment.certificate_code}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Could not generate PDF. Please try again.');
    }
  }

  if (enrollment.certificate_code) {
    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return (
      <div className="cert-print-root">
      <div className="card" style={{ padding: 32 }}>
        <div className="cert-frame" ref={certRef} style={{ animation: 'popIn .4s ease' }}>
          <div className="cert-corner tl" /><div className="cert-corner tr" />
          <div className="cert-corner bl" /><div className="cert-corner br" />
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: 'var(--muted)', marginBottom: 18 }}>Certificate of Completion</div>
          <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 12, color: 'var(--yellow)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <svg viewBox="0 0 14 14" fill="none" style={{ width: 14, height: 14 }}><polygon points="7,1 13,13 1,13" fill={course.accent_color || '#F7C91B'} /></svg> AI FOUNDRY
          </div>
          <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 26, lineHeight: 1.2, marginBottom: 18, color: 'var(--ink)' }}>Certificate of Completion</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>This is to certify that</div>
          <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 22, color: 'var(--orange)', marginBottom: 6 }}>{toTitleCase(user?.full_name || 'Student')}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 6 }}>has successfully completed</div>
          <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 20 }}>{toTitleCase(course.title)}</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'DM Mono,monospace', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Date Issued</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{today}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--muted)', fontFamily: 'DM Mono,monospace', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Credential ID</div>
              <div style={{ fontSize: 13, fontWeight: 600, fontFamily: 'DM Mono,monospace' }}>{enrollment.certificate_code}</div>
            </div>
          </div>
          <div style={{ width: 160, margin: '0 auto', borderTop: '1.5px solid var(--border)', paddingTop: 10 }}>
            <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 700, fontSize: 13 }}>{toTitleCase(course.instructor || 'Instructor')}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>Course Instructor</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 20, display: 'flex', justifyContent: 'center', gap: 8 }}>
          <button className="btn btn-p btn-sm" onClick={handleDownloadPDF}>↓ Download PDF</button>
          <button className="btn btn-o btn-sm" onClick={handlePrint}>🖨 Print</button>
        </div>
      </div>
      </div>
    );
  }

  const eligible = allModulesDone && allQuizzesPassed && allAssignmentsSubmitted && allAssignmentsGraded;

  const checks = [
    { label: 'Complete all modules',      done: allModulesDone },
    { label: 'Pass all quizzes',          done: allQuizzesPassed },
    { label: 'Submit all assignments',    done: allAssignmentsSubmitted },
    { label: 'All assignments graded',    done: allAssignmentsGraded },
  ];

  return (
    <div className="card" style={{ textAlign: 'center', padding: 40 }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🎓</div>
      <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 18, marginBottom: 6 }}>
        {eligible ? "You're eligible for your certificate!" : 'Certificate Requirements'}
      </div>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
        {eligible
          ? 'All requirements met. Your certificate will be issued by the instructor.'
          : 'Complete the following requirements to earn your course certificate.'}
      </p>
      <div style={{ maxWidth: 380, margin: '0 auto', textAlign: 'left', marginBottom: 24 }}>
        {checks.map((ch, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
              ...(ch.done ? { background: 'var(--green)', color: 'white' } : { background: 'var(--surface)', border: '2px solid var(--border)', color: 'var(--muted)' }),
            }}>
              {ch.done ? '✓' : ''}
            </div>
            <span style={{ fontSize: 13, color: ch.done ? 'var(--ink)' : 'var(--muted)' }}>{ch.label}</span>
            <span className={`b ${ch.done ? 'b-g' : 'b-gr'}`} style={{ marginLeft: 'auto' }}>{ch.done ? 'Done' : 'Pending'}</span>
          </div>
        ))}
      </div>
      <button className="btn btn-o btn-sm" onClick={onGoModules}>Continue Learning →</button>
    </div>
  );
}
